import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Role } from '@/api/types';
import { login as apiLogin, logout as apiLogout, me } from '@/api/auth';

type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  user: User | null;
  token: string | null;
  status: AuthStatus;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
  /** Local-only reset used by the 401 interceptor — no API call. */
  clear: () => void;

  // Selectors
  isLoggedIn: () => boolean;
  hasRole: (role: Role) => boolean;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  isPlatformAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      status: 'idle',

      login: async (email, password) => {
        const { user, token } = await apiLogin(email, password);
        set({ user, token, status: 'authenticated' });
      },

      logout: async () => {
        try {
          await apiLogout();
        } catch {
          // Ignore errors — token may already be gone.
        }
        set({ user: null, token: null, status: 'unauthenticated' });
      },

      bootstrap: async () => {
        if (!get().token) {
          set({ status: 'unauthenticated' });
          return;
        }
        set({ status: 'loading' });
        try {
          const { user } = await me();
          set({ user, status: 'authenticated' });
        } catch {
          set({ user: null, token: null, status: 'unauthenticated' });
        }
      },

      clear: () => {
        set({ user: null, token: null, status: 'unauthenticated' });
      },

      isLoggedIn: () => get().status === 'authenticated',

      hasRole: (role) => get().user?.role === role,

      // admin covers both admin and super_admin
      isAdmin: () => {
        const role = get().user?.role;
        return role === 'admin' || role === 'super_admin';
      },

      isSuperAdmin: () => get().user?.role === 'super_admin',

      isPlatformAdmin: () => {
        const role = get().user?.role;
        return role === 'platform_admin' || role === 'super_admin';
      },
    }),
    {
      name: 'senseface-auth',
      partialize: (state) => ({ token: state.token }),
    },
  ),
);
